import type { PublicConfig, Lead, LeadOption, Model, Category, Option, Constraint } from '@/types'
import { getAdminToken } from '@/store/auth-store'

const BASE = (import.meta.env.VITE_API_URL ?? '') + '/api'

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (url.startsWith('/admin')) {
    const token = getAdminToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  const res = await fetch(`${BASE}${url}`, {
    headers,
    ...init,
  })
  const json = await res.json() as { success: boolean; data?: T; error?: string }
  if (!json.success) throw new Error(json.error ?? 'Request failed')
  return json.data as T
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export async function loginAdmin(username: string, password: string): Promise<{ token: string }> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  const json = await res.json() as { success: boolean; data?: { token: string }; error?: string }
  if (!json.success) throw new Error(json.error ?? 'Login failed')
  return json.data!
}

// ─── Public ──────────────────────────────────────────────────────────────────

export function fetchConfig(): Promise<PublicConfig> {
  return request<PublicConfig>('/public/config')
}

export function submitLead(payload: {
  model_id: number
  option_ids: number[]
  snapshot_url?: string
  customer: {
    name: string
    email: string
    phone: string
    location?: string
    timeline?: string
    budget_range?: string
    financing_interest?: boolean
    contact_method?: string
    notes?: string
  }
}): Promise<{ lead_id: number; estimated_total: number; message: string }> {
  return request('/public/leads', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function uploadSnapshot(blob: Blob): Promise<{ url: string }> {
  const form = new FormData()
  form.append('file', blob, 'snapshot.jpg')
  const res = await fetch(`${BASE}/public/snapshots`, {
    method: 'POST',
    body: form,
  })
  const json = await res.json() as { success: boolean; data?: { url: string }; error?: string }
  if (!json.success) throw new Error(json.error ?? 'Snapshot upload failed')
  return json.data!
}

// ─── Admin — Leads ────────────────────────────────────────────────────────────

export function fetchDashboardStats(): Promise<{
  total_leads: number
  new_leads: number
  avg_build_value: number
  top_model: string | null
  monthly_lead_value: number
  recent_leads: Partial<Lead>[]
}> {
  return request('/admin/dashboard')
}

export function fetchLeads(params?: {
  status?: string
  page?: number
}): Promise<{ leads: Lead[]; total: number; page: number; pages: number }> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.page) qs.set('page', String(params.page))
  return request(`/admin/leads?${qs}`)
}

export function fetchLeadById(id: number): Promise<{ lead: Lead; options: LeadOption[] }> {
  return request(`/admin/leads/${id}`)
}

export function updateLeadStatus(id: number, status: string): Promise<{ id: number; status: string }> {
  return request(`/admin/leads/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  })
}

export function updateLeadAdminNotes(id: number, admin_notes: string): Promise<{ id: number; admin_notes: string | null }> {
  return request(`/admin/leads/${id}/notes`, {
    method: 'PATCH',
    body: JSON.stringify({ admin_notes }),
  })
}

// ─── Admin — Settings ─────────────────────────────────────────────────────────

export function fetchAdminSettings(): Promise<Record<string, string>> {
  return request('/admin/settings')
}

export function updateSettings(updates: Record<string, string>): Promise<Record<string, string>> {
  return request('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

// ─── Admin — Models ───────────────────────────────────────────────────────────

export function fetchAdminModels(): Promise<Model[]> {
  return request('/admin/models')
}

export function createModel(data: Partial<Model>): Promise<Model> {
  return request('/admin/models', { method: 'POST', body: JSON.stringify(data) })
}

export function updateModel(id: number, data: Partial<Model>): Promise<Model> {
  return request(`/admin/models/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export function deleteModel(id: number): Promise<void> {
  return request(`/admin/models/${id}`, { method: 'DELETE' })
}

// ─── Admin — Categories ───────────────────────────────────────────────────────

export function fetchAdminCategories(): Promise<Category[]> {
  return request('/admin/categories')
}

export function createCategory(data: Partial<Category>): Promise<Category> {
  return request('/admin/categories', { method: 'POST', body: JSON.stringify(data) })
}

export function updateCategory(id: number, data: Partial<Category>): Promise<Category> {
  return request(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export function deleteCategory(id: number): Promise<void> {
  return request(`/admin/categories/${id}`, { method: 'DELETE' })
}

// ─── Admin — Options ──────────────────────────────────────────────────────────

export type OptionWithModels = Option & { model_ids: number[] }

export function fetchAdminOptions(): Promise<OptionWithModels[]> {
  return request('/admin/options')
}

export function createOption(data: Partial<Option> & { model_ids: number[] }): Promise<OptionWithModels> {
  return request('/admin/options', { method: 'POST', body: JSON.stringify(data) })
}

export function updateOption(id: number, data: Partial<Option> & { model_ids: number[] }): Promise<OptionWithModels> {
  return request(`/admin/options/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export function updateOptionPrice(id: number, price: number): Promise<{ id: number; price: number }> {
  return request(`/admin/options/${id}/price`, {
    method: 'PATCH',
    body: JSON.stringify({ price }),
  })
}

export function deleteOption(id: number): Promise<void> {
  return request(`/admin/options/${id}`, { method: 'DELETE' })
}

// ─── Admin — Analytics ───────────────────────────────────────────────────────

export function fetchAdminAnalytics(): Promise<{
  monthly: { month: string; leads: number; revenue: number }[]
  model_breakdown: { model: string; count: number }[]
  top_options: { name: string; count: number }[]
  status_breakdown: { status: string; count: number }[]
  conversion_stats: { total: number; converted: number; avg_value: number }
  funnel: { event_type: string; sessions: number }[]
}> {
  return request('/admin/analytics')
}

export function getLeadPDFUrl(id: number): string {
  const token = getAdminToken()
  const qs = token ? `?token=${encodeURIComponent(token)}` : ''
  return `${BASE}/admin/leads/${id}/pdf${qs}`
}

export function getLeadsExportUrl(status?: string): string {
  const token = getAdminToken()
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (token) params.set('token', token)
  const qs = params.toString() ? `?${params}` : ''
  return `${BASE}/admin/leads/export${qs}`
}

// ─── Admin — Constraints ──────────────────────────────────────────────────────

export function fetchAdminConstraints(): Promise<Constraint[]> {
  return request('/admin/constraints')
}

export function createConstraint(data: Partial<Constraint>): Promise<Constraint> {
  return request('/admin/constraints', { method: 'POST', body: JSON.stringify(data) })
}

export function updateConstraint(id: number, data: Partial<Constraint>): Promise<Constraint> {
  return request(`/admin/constraints/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export function deleteConstraint(id: number): Promise<void> {
  return request(`/admin/constraints/${id}`, { method: 'DELETE' })
}

// ─── Admin — Assets ───────────────────────────────────────────────────────────

export async function uploadAsset(file: File): Promise<{ key: string; url: string }> {
  const token = getAdminToken()
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${BASE}/admin/assets/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const json = await res.json() as { success: boolean; data?: { key: string; url: string }; error?: string }
  if (!json.success) throw new Error(json.error ?? 'Upload failed')
  return json.data!
}

// ─── Admin — Pipeline ─────────────────────────────────────────────────────────

export interface PipelineJob {
  id: string
  model_id: number | null
  model_name: string
  input_key: string
  input_ext: string
  output_key: string | null
  glb_url: string | null
  wall_height: number
  width_ft: number | null
  depth_ft: number | null
  status: 'pending' | 'processing' | 'done' | 'failed'
  log: string | null
  created_at: string
  updated_at: string
}

export async function createPipelineJob(params: {
  file: File
  model_id?: number | null
  model_name: string
  wall_height: number
  width_ft?: number
  depth_ft?: number
}): Promise<{ id: string; status: string; input_key: string }> {
  const token = getAdminToken()
  const form = new FormData()
  form.append('file', params.file)
  form.append('model_name', params.model_name)
  form.append('wall_height', String(params.wall_height))
  if (params.model_id != null) form.append('model_id', String(params.model_id))
  if (params.width_ft != null) form.append('width_ft', String(params.width_ft))
  if (params.depth_ft != null) form.append('depth_ft', String(params.depth_ft))

  const res = await fetch(`${BASE}/admin/pipeline/jobs`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const json = await res.json() as { success: boolean; data?: { id: string; status: string; input_key: string }; error?: string }
  if (!json.success) throw new Error(json.error ?? 'Failed to create job')
  return json.data!
}

export function fetchPipelineJobs(status?: string): Promise<PipelineJob[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return request(`/admin/pipeline/jobs${qs}`)
}

export function fetchPipelineJob(id: string): Promise<PipelineJob> {
  return request(`/admin/pipeline/jobs/${id}`)
}

export function retryPipelineJob(id: string): Promise<void> {
  return request(`/admin/pipeline/jobs/${id}/retry`, { method: 'POST' })
}

export function deletePipelineJob(id: string): Promise<void> {
  return request(`/admin/pipeline/jobs/${id}`, { method: 'DELETE' })
}

export async function uploadGlbAsset(file: File): Promise<{ key: string; url: string }> {
  const token = getAdminToken()
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${BASE}/admin/assets/upload-glb`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  const json = await res.json() as { success: boolean; data?: { key: string; url: string }; error?: string }
  if (!json.success) throw new Error(json.error ?? 'GLB upload failed')
  return json.data!
}

export function getAssetUrl(urlOrKey: string): string {
  if (urlOrKey.startsWith('http')) return urlOrKey
  // Relative paths from the worker (e.g. /api/public/assets/...) need the API host prepended
  if (urlOrKey.startsWith('/api/')) return `${import.meta.env.VITE_API_URL ?? ''}${urlOrKey}`
  return `${BASE}/public/assets/${urlOrKey}`
}
